import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StockTableLayout, Statistic } from '../../components/StockTable';
import { useAnalystViews, transformAnalystViewsData } from '../../hooks/useAnalystViews';
import { syncApiService } from '../../services/syncApi';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import { userTickerListApi, UserTickerList, UserTickerListWithTickers } from '../../services/userTickerListApi';
import { userApi } from '../../services/userApi';
import { usageLimitsApi } from '../../services/usageLimitsApi';
import { AnalystViewDisplayData } from '../../types/analystViews';
import TabBar from '../../components/Common/TabBar';
import { ANALYST_VIEWS_STATISTICS, ANALYST_VIEWS_CATEGORY_HEADERS } from '../../utils/analystViewsTabUtils';
import { formatAnalystViewsStatisticValue, getAnalystViewsStatisticColor } from '../../hooks/useAnalystViewsTabStatistics';
import { LockIcon } from '../../components/StockTable/StockTable';
import DeleteListConfirmationModal from '../../components/Modals/DeleteListConfirmationModal';
import ErrorDialog from '../../components/Modals/ErrorDialog';
import ShareListModal from '../../components/ShareListModal';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import CategoryInfoModal from '../../components/CategoryInfoModal/CategoryInfoModal';
import IndicatorInfoModal from '../../components/IndicatorInfoModal/IndicatorInfoModal';
import { 
  cacheUserLists, 
  getCachedUserLists, 
  clearListCache,
  clearUserListsCache,
  setCacheInvalidation,
  cacheCurrentListName,
  getCachedCurrentListName,
  clearCurrentListNameCache,
  cacheCurrentListId,
  getCachedCurrentListId,
  clearCurrentListIdCache,
  generateUniqueListName
} from '../../utils/listCache';
import { sessionSaveService } from '../../services/sessionSaveService';
import { useEditMode } from '../../hooks/useEditMode';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useModals } from '../../hooks/useModals';
import { useUserListManagement } from '../../hooks/useUserListManagement';
import { useListPreferences } from '../../hooks/useListPreferences';
import { useAddTicker } from '../../hooks/useAddTicker';
import { publicListApi, PublicListTickerMapping, PublicListPreferences } from '../../services/publicListApi';

// Default tickers to display
const DEFAULT_TICKERS = ['NVDA', 'AAPL', 'GOOGL', 'MSFT', 'AMZN', 'META', 'TSLA'];

const ListAnalystViews: React.FC = () => {
  // Set page title
  useEffect(() => {
    document.title = 'Stock Table - Analysts';
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  
  // Pro-only statistics (always show PRO tag, regardless of subscription status)
  const PRO_ONLY_STATISTICS: string[] = [];
  
  // Check for public_uuid query parameter
  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const isPublicList = !!publicUuid;
  
  // Public list state
  const [publicListData, setPublicListData] = useState<{
    publicListName: string | null;
    tickerMappings: PublicListTickerMapping[];
    listPreferences: PublicListPreferences | null;
    ownerHasProSubscription: boolean;
  } | null>(null);
  
  // Determine locked statistics based on pro subscription status (for actually locking values)
  // For public lists, use owner's subscription status instead of viewer's
  const lockedStatistics = React.useMemo(() => {
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription ? [] : PRO_ONLY_STATISTICS;
    }
    return (!isAuthenticated || !user?.hasProSubscription) ? PRO_ONLY_STATISTICS : [];
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);
  const [isLoadingPublicList, setIsLoadingPublicList] = useState(false);
  const [publicListError, setPublicListError] = useState<string | null>(null);
  const [isPublicListNotFound, setIsPublicListNotFound] = useState(false);
  
  // UI state
  const [isEditingTickers, setIsEditingTickers] = useState(false);
  const [tickerList, setTickerList] = useState<string[]>(() => {
    // For public lists, start with empty list - will be populated from public list data
    if (isPublicList) {
      return [];
    }
    // For authenticated users, start with empty list to avoid default ticker API calls
    if (isAuthenticated) {
      return [];
    }
    // For unauthenticated users, only use session storage if it exists, otherwise start empty
    // Default tickers should only be shown on the Landing page
    const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
    return savedTickers ? JSON.parse(savedTickers) : [];
  });
  
  
  const [listName, setListName] = useState<string>(() => {
    // For public lists, check sessionStorage for cached public list name
    if (isPublicList && publicUuid) {
      const cachedPublicListName = sessionStorage.getItem(`public-list-name-${publicUuid}`);
      if (cachedPublicListName) {
        return cachedPublicListName;
      }
      // Return empty string if no cached name, will be set when data loads
      return '';
    }
    
    // For authenticated users, try to get cached list name first
    if (isAuthenticated) {
      const cachedListName = getCachedCurrentListName();
      if (cachedListName) {
        return cachedListName;
      }
    }
    
    // Fallback to session storage or default
    const savedListName = sessionStorage.getItem('stocktable-list-name');
    return savedListName || 'Main List';
  });
  
  // Sync status state
  const [syncStatuses, setSyncStatuses] = useState<Record<string, string>>({});
  
  // Refs to track when we need to reload data
  const refreshUsageLimitsRef = useRef<(() => void) | null>(null);
  
  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isIndicatorModalOpen, setIsIndicatorModalOpen] = useState(false);
  const [selectedIndicatorKey, setSelectedIndicatorKey] = useState<string>('');

  // Store original session storage values to restore when leaving public list
  const originalSessionStorageRef = useRef<{
    tickerList: string | null;
    listName: string | null;
    hiddenStatistics: string | null;
  } | null>(null);

  // Track if we've attempted to fetch the public list (to prevent infinite retries)
  const publicListFetchAttemptedRef = useRef<string | null>(null);
  
  // Fetch public list data when publicUuid is present
  useEffect(() => {
    // Only fetch if we haven't already attempted for this UUID, or if it's a different UUID
    const shouldFetch = publicUuid && 
                       !publicListData && 
                       !isLoadingPublicList && 
                       publicListFetchAttemptedRef.current !== publicUuid;
    
    if (shouldFetch) {
      // Save original session storage values before loading public list (only for unauthenticated users)
      if (!isAuthenticated && !originalSessionStorageRef.current) {
        originalSessionStorageRef.current = {
          tickerList: sessionStorage.getItem('stocktable-ticker-list'),
          listName: sessionStorage.getItem('stocktable-list-name'),
          hiddenStatistics: sessionStorage.getItem('stocktable-hidden-statistics'),
        };
      }

      // Mark that we're attempting to fetch this UUID
      publicListFetchAttemptedRef.current = publicUuid;
      setIsLoadingPublicList(true);
      setPublicListError(null);
      
      publicListApi.getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            publicListName: response.data.public_list_name,
            tickerMappings: response.data.ticker_mappings,
            listPreferences: response.data.list_preferences,
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });
          
          // Update ticker list from mappings
          const tickers = response.data.ticker_mappings
            .sort((a, b) => a.order_num - b.order_num)
            .map(mapping => mapping.ticker_symbol);
          setTickerList(tickers);
          
          // Update list name and cache it for tab switching
          const publicListName = response.data.public_list_name || 'Public List';
          setListName(publicListName);
          // Cache the public list name in sessionStorage for immediate restoration on tab switch
          if (publicUuid) {
            sessionStorage.setItem(`public-list-name-${publicUuid}`, publicListName);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch public list:', error);
          const errorMessage = error.message || 'Failed to load public list';
          setPublicListError(errorMessage);
          
          // For 404 or 403 errors, set flag to show not found/not shared message instead of table
          if ((error as any).status === 404 || errorMessage.includes('not found')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list does not exist or is no longer available.');
          } else if ((error as any).status === 403 || errorMessage.includes('not publicly shared')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list is not publicly shared.');
          }
        })
        .finally(() => {
          setIsLoadingPublicList(false);
        });
    }
    
    // Reset the attempted ref and not found flag when publicUuid changes or becomes null
    if (!publicUuid) {
      publicListFetchAttemptedRef.current = null;
      setIsPublicListNotFound(false);
    }
  }, [publicUuid, publicListData, isLoadingPublicList, isAuthenticated]);

  // Restore session storage on component unmount if we were viewing a public list
  useEffect(() => {
    return () => {
      // Cleanup: restore original session storage when component unmounts if we were in public list mode
      if (originalSessionStorageRef.current && !isAuthenticated) {
        const original = originalSessionStorageRef.current;
        // Restore synchronously before component unmounts
        if (original.tickerList !== null) {
          sessionStorage.setItem('stocktable-ticker-list', original.tickerList);
        } else {
          sessionStorage.removeItem('stocktable-ticker-list');
        }
        if (original.listName !== null) {
          sessionStorage.setItem('stocktable-list-name', original.listName);
        } else {
          sessionStorage.removeItem('stocktable-list-name');
        }
        if (original.hiddenStatistics !== null) {
          sessionStorage.setItem('stocktable-hidden-statistics', original.hiddenStatistics);
        } else {
          sessionStorage.removeItem('stocktable-hidden-statistics');
        }
        originalSessionStorageRef.current = null;
      }
    };
  }, [isAuthenticated]);

  // Reset not found flag and clear error when publicUuid becomes null (e.g., when clicking logo)
  useEffect(() => {
    if (!publicUuid) {
      setIsPublicListNotFound(false);
      setPublicListError(null);
      setPublicListData(null);
    }
  }, [publicUuid]);

  // Also restore when location changes away from public list (before unmount)
  // Watch location.search directly to catch URL changes even when component doesn't unmount
  useEffect(() => {
    // If we're no longer in public list mode, restore from session storage
    if (!publicUuid && !isAuthenticated) {
      // If we have stored original values, restore them
      if (originalSessionStorageRef.current) {
        const original = originalSessionStorageRef.current;
        
        // Restore session storage
        if (original.tickerList !== null) {
          sessionStorage.setItem('stocktable-ticker-list', original.tickerList);
        } else {
          sessionStorage.removeItem('stocktable-ticker-list');
        }
        if (original.listName !== null) {
          sessionStorage.setItem('stocktable-list-name', original.listName);
        } else {
          sessionStorage.removeItem('stocktable-list-name');
        }
        if (original.hiddenStatistics !== null) {
          sessionStorage.setItem('stocktable-hidden-statistics', original.hiddenStatistics);
        } else {
          sessionStorage.removeItem('stocktable-hidden-statistics');
        }
        
        // Reset component state to match restored session storage
        if (original.tickerList !== null) {
          try {
            const restoredTickers = JSON.parse(original.tickerList);
            setTickerList(restoredTickers);
          } catch (e) {
            // If parsing fails, start with empty list (default tickers only on Landing page)
            setTickerList([]);
          }
        } else {
          // Start with empty list if no original data (default tickers only on Landing page)
          setTickerList([]);
        }
        
        if (original.listName !== null) {
          setListName(original.listName);
        } else {
          const savedListName = sessionStorage.getItem('stocktable-list-name');
          setListName(savedListName || 'Main List');
        }
        
        originalSessionStorageRef.current = null;
      } else {
        // If we don't have original values saved, restore from current session storage
        const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
        if (savedTickers) {
          try {
            const restoredTickers = JSON.parse(savedTickers);
            setTickerList(restoredTickers);
          } catch (e) {
            // If parsing fails, start with empty list (default tickers only on Landing page)
            setTickerList([]);
          }
        } else {
          // Start with empty list if no session storage (default tickers only on Landing page)
          setTickerList([]);
        }
        
        const savedListName = sessionStorage.getItem('stocktable-list-name');
        setListName(savedListName || 'Main List');
      }
      
      // Clear public list data
      setPublicListData(null);
      setPublicListError(null);
      setIsPublicListNotFound(false);
    }
  }, [publicUuid, location.search, isAuthenticated]);

  // User list management hook (only for non-public lists)
  const {
    userLists,
    currentListId,
    isLoadingUserLists,
    setUserLists,
    setCurrentListId,
    needsUserListsReload,
    needsListDataReload,
    handleListChange,
    handleCreateNewList,
    handleCreateNewListWithName,
  } = useUserListManagement({
    isAuthenticated: isAuthenticated && !isPublicList,
    tickerList,
    listName,
    setTickerList,
    setListName,
    refreshUsageLimitsRef,
    returnPath: '/analysts',
  });

  // List preferences hook (only for non-public lists)
  const {
    hiddenStatistics: privateHiddenStatistics,
    setHiddenStatistics,
    handleHideStatistic,
    handleShowStatistic,
    savePreferences,
  } = useListPreferences({
    isAuthenticated: isAuthenticated && !isPublicList,
    currentListId: isPublicList ? null : currentListId,
    pageType: 'analysts',
    persistToSessionStorageImmediately: true, // Match previous behavior
  });

  // Determine which lists are locked (beyond 3rd position for free users)
  // Free users can only access the first 3 lists (sorted alphabetically)
  const sortedUserLists = useMemo(() => {
    return [...userLists].sort((a, b) => {
      const nameA = a.list_name.toLowerCase();
      const nameB = b.list_name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [userLists]);

  const lockedListIds = useMemo(() => {
    if (isAuthenticated && !user?.hasProSubscription && sortedUserLists.length > 3) {
      // Lists beyond the 3rd position (index 3+) are locked
      return sortedUserLists.slice(3).map(list => list.id);
    }
    return [];
  }, [sortedUserLists, isAuthenticated, user?.hasProSubscription]);

  // Check if current list is locked
  const isCurrentListLocked = useMemo(() => {
    return currentListId !== null && lockedListIds.includes(currentListId);
  }, [currentListId, lockedListIds]);

  // Use public list preferences if in public list mode, otherwise use private preferences
  const hiddenStatistics = isPublicList && publicListData?.listPreferences?.analyst_indicators_to_hide
    ? publicListData.listPreferences.analyst_indicators_to_hide
    : privateHiddenStatistics;

  const [isSyncing, setIsSyncing] = useState(false);
  // Initialize syncingTickers from sessionStorage to restore state after refresh
  // Note: currentListId is not available yet at initialization, so we use global key initially
  // The helper function will use list ID-specific key when actually reading/writing
  const [syncingTickers, setSyncingTickers] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem('stocktable-syncing-tickers');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [unsyncedTickers, setUnsyncedTickers] = useState<Set<string>>(new Set());
  const [pollingLimitReached, setPollingLimitReached] = useState<Set<string>>(new Set());
  
  // Refs for polling management to prevent stale closures and race conditions
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // Use ref for synchronous access
  const isCheckingSyncRef = useRef(false); // Flag to prevent concurrent sync checks
  const currentAttemptRef = useRef(1); // Track current attempt number for interval callback
  const hasRestoredSyncingStateRef = useRef(false); // Flag to track if we've restored syncing state on initial load
  // Ref to prevent recoverPendingTickers from running multiple times
  const hasRecoveredPendingTickersRef = useRef(false);

  // Auto-save function for authenticated users (defined before handleSaveChanges)
  const autoSaveList = useCallback(async (tickers: string[]) => {
    // Don't auto-save in public list mode
    if (isPublicList || !isAuthenticated) return;
    
    // Determine which list ID to use
    let listId = currentListId;
    
    // If currentListId is null, try to get it from userLists or wait for it
    if (listId === null) {
      // First, try to get from cache (might be set by createDefaultMainListIfNeeded)
      const { getCachedCurrentListId } = await import('../../utils/listCache');
      const cachedListId = getCachedCurrentListId();
      if (cachedListId) {
        console.log('💾 Using cached list ID for auto-save:', cachedListId);
        listId = cachedListId;
      } else if (userLists.length > 0) {
        // If userLists has items, use the first list (or try to get default list)
        // Try to get the user's default list
        try {
          const defaultListResponse = await userApi.getDefaultList();
          if (defaultListResponse.defaultUserTickerListId) {
            const defaultListExists = userLists.some(list => list.id === defaultListResponse.defaultUserTickerListId);
            if (defaultListExists) {
              listId = defaultListResponse.defaultUserTickerListId;
            } else {
              // Default list doesn't exist in userLists, use first list
              listId = userLists[0].id;
            }
          } else {
            // No default list set, use first list
            listId = userLists[0].id;
          }
        } catch (error) {
          // If we can't get default list, use first list
          console.log('⚠️ Could not get default list, using first list:', error);
          listId = userLists[0].id;
        }
      } else {
        // No lists available yet - ticker is saved to sessionStorage and will be recovered when lists load
        console.log('⚠️ Auto-save skipped: currentListId is null and no userLists available. Ticker saved to sessionStorage.');
        return;
      }
    }
    
    // Ensure listId is not null before proceeding
    if (!listId) {
      console.log('⚠️ Auto-save skipped: No list ID available');
      return;
    }
    
    try {
      console.log('💾 Auto-saving list for authenticated user:', { listId, tickers });
      const tickerMappings = tickers.map((ticker, index) => ({
        ticker_symbol: ticker,
        order_num: index + 1
      }));
      
      await userTickerListApi.updateTickerMappings(listId, tickerMappings);
      console.log('✅ Auto-save successful');
      
      // Clear the cache for this list to force reload on tab switch
      clearListCache(listId);
      console.log('🗑️ Cleared cache for list', listId);
      
      // Mark that we need to reload list data to ensure fresh data is loaded
      needsListDataReload.current = true;
      console.log('🔄 Marked list data for reload');
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
    }
  }, [isAuthenticated, currentListId, isPublicList, userLists]);

  // Helper function to get sessionStorage key based on list ID
  const getTickerListKey = useCallback(() => {
    return isAuthenticated && currentListId !== null 
      ? `stocktable-ticker-list-${currentListId}` 
      : 'stocktable-ticker-list';
  }, [isAuthenticated, currentListId]);

  // Helper function to get syncing tickers sessionStorage key based on list ID
  const getSyncingTickersKey = useCallback(() => {
    return isAuthenticated && currentListId !== null 
      ? `stocktable-syncing-tickers-${currentListId}` 
      : 'stocktable-syncing-tickers';
  }, [isAuthenticated, currentListId]);

  // Handle save changes (defined after autoSaveList so it can reference it)
  const handleSaveChanges = useCallback(async (saveTempTickerList: string[], saveTempListName: string) => {
    // Don't allow saving changes in public list mode
    if (isPublicList) {
      return;
    }
    
    // Deduplicate the list before saving
    const newTickerList = Array.from(new Set([...saveTempTickerList]));

    if (!isAuthenticated) {
      sessionStorage.setItem(getTickerListKey(), JSON.stringify(newTickerList));
    }

    setTickerList(newTickerList);
    setListName(saveTempListName);
    setIsEditingTickers(false);
    
    // Auto-save for authenticated users
    if (isAuthenticated && currentListId) {
      try {
        // Update list name if it changed
        if (saveTempListName !== listName) {
          console.log('📝 Updating list name:', { listId: currentListId, oldName: listName, newName: saveTempListName });
          await userTickerListApi.updateListName(currentListId, saveTempListName);
          console.log('✅ List name updated successfully');
          
          // Update the user lists cache to reflect the name change
          setUserLists((prev: UserTickerList[]) => prev.map((list: UserTickerList) => 
            list.id === currentListId 
              ? { ...list, list_name: saveTempListName }
              : list
          ));
          
          // Cache the updated list name to prevent flickering on tab switch
          cacheCurrentListName(saveTempListName);
          console.log('🗑️ Cached updated list name to prevent flickering');
        }
        
        // Update ticker mappings
        await autoSaveList(newTickerList);
        
        // Refresh usage limits after successful mappings update
        try {
          await Promise.all([
            usageLimitsApi.checkTickerLimit(),
            usageLimitsApi.checkListLimit()
          ]);
          console.log('✅ Usage limits refreshed after mappings update');
        } catch (error) {
          console.error('Failed to refresh usage limits:', error);
        }
        
        // Save user list preferences for analyst indicators
        try {
          await savePreferences(hiddenStatistics);
        } catch (error) {
          console.error('❌ Failed to save analyst preferences:', error);
          // Don't throw error to prevent UI from breaking
        }
      } catch (error) {
        console.error('❌ Failed to update list:', error);
      }
    } else {
      // Save to session storage for unauthenticated users
      sessionStorage.setItem('stocktable-list-name', saveTempListName);
      // Preferences are already saved via savePreferences in the hook
      await savePreferences(hiddenStatistics);
    }
    
    // Note: sessionStorage is already updated above before state changes
    // This prevents the recovery mechanism from restoring removed tickers
  }, [tickerList, listName, isAuthenticated, currentListId, setTickerList, setListName, setIsEditingTickers, hiddenStatistics, setUserLists, autoSaveList, savePreferences]);

  // Edit mode hook
  const {
    tempTickerList,
    tempListName,
    setTempTickerList,
    setTempListName,
    handleStartEdit,
    handleCancelEdit,
    handleDoneEdit,
    handleSave,
  } = useEditMode({
    tickerList,
    listName,
    setTickerList,
    setListName,
    isEditingTickers,
    setIsEditingTickers,
    returnPath: '/analysts',
    onSaveChanges: handleSaveChanges,
    hiddenStatistics: !isPublicList ? { analystViews: privateHiddenStatistics } : undefined,
  });

  // Drag and drop hook (ListAnalystViews uses 'swap' behavior)
  const {
    draggedTicker,
    dragOverTicker,
    droppedTicker,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragAndDrop(
    {
      tempTickerList,
      setTempTickerList,
      isEditingTickers,
    },
    'swap'
  );

  // Determine which ticker list to use for display
  const displayTickerList = isEditingTickers ? tempTickerList : tickerList;

  // Determine locked tickers (beyond 10th position for free users OR all tickers if list is locked)
  // Free users can only access the first 10 tickers in a list
  // If the list itself is locked, all tickers are locked
  // Note: Calculate after tempTickerList is available from useEditMode hook
  // For public lists, use owner's subscription status instead of viewer's
  const lockedTickers = useMemo(() => {
    if (isCurrentListLocked) {
      // If list is locked, lock all tickers
      return displayTickerList;
    }
    // For public lists, use owner's subscription status
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription 
        ? [] // Owner has PRO, so no locked tickers
        : displayTickerList.slice(10); // Owner doesn't have PRO, apply same locks
    }
    // For private lists, use viewer's subscription status
    return (!isAuthenticated || !user?.hasProSubscription) 
      ? displayTickerList.slice(10) // Tickers beyond the 10th position (index 10+)
      : [];
  }, [displayTickerList, isAuthenticated, user?.hasProSubscription, isCurrentListLocked, isPublicList, publicListData]);

  // Persist ticker list (only for non-authenticated users)
  // Always deduplicate before saving to prevent duplicates
  useEffect(() => {
    if (!isAuthenticated && !isPublicList) {
      const uniqueTickerList = Array.from(new Set(tickerList));
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(uniqueTickerList));
    }
  }, [tickerList, isAuthenticated, isPublicList]);
  
  // Ensure tickerList is always deduplicated (for authenticated users too)
  useEffect(() => {
    if (tickerList.length > 0) {
      const uniqueTickerList = Array.from(new Set(tickerList));
      if (uniqueTickerList.length !== tickerList.length) {
        console.log(`🔧 ListAnalystViews: Deduplicating tickerList: ${tickerList.length} -> ${uniqueTickerList.length}`);
        setTickerList(uniqueTickerList);
      }
    }
  }, [tickerList]);


  
  // Local state for analyst views data
  const [analystViewsData, setAnalystViewsData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);



  // Function to fetch analyst views data
  const fetchAnalystViewsData = async (tickers: string[], syncResponseData?: any) => {
    if (tickers.length === 0) {
      setAnalystViewsData([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // If syncResponseData is not provided, fetch it to get last_sync_ended_at
      if (!syncResponseData) {
        const api = isAuthenticated ? userTickerApi : tickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: tickers,
        });
        
        // Calculate last updated from latest last_sync_ended_at of synced tickers
        if (syncResponse.success && syncResponse.data && syncResponse.data.tickers) {
          const syncedTickersWithDate = syncResponse.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
          if (syncedTickersWithDate.length > 0) {
            const lastSyncDates = syncedTickersWithDate
              .map((t: any) => {
                const date = typeof t.last_sync_ended_at === 'string' 
                  ? new Date(t.last_sync_ended_at) 
                  : t.last_sync_ended_at;
                return date instanceof Date && !isNaN(date.getTime()) ? date : null;
              })
              .filter((d: Date | null): d is Date => d !== null);
            
            if (lastSyncDates.length > 0) {
              const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
              setLastUpdated(latestDate);
            }
          }
        }
      } else if (syncResponseData && syncResponseData.data && syncResponseData.data.tickers) {
        // Calculate last updated from provided sync response data
        const syncedTickersWithDate = syncResponseData.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
        if (syncedTickersWithDate.length > 0) {
          const lastSyncDates = syncedTickersWithDate
            .map((t: any) => {
              const date = typeof t.last_sync_ended_at === 'string' 
                ? new Date(t.last_sync_ended_at) 
                : t.last_sync_ended_at;
              return date instanceof Date && !isNaN(date.getTime()) ? date : null;
            })
            .filter((d: Date | null): d is Date => d !== null);
          
          if (lastSyncDates.length > 0) {
            const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
            setLastUpdated(latestDate);
          }
        }
      }

      const request = {
        ticker_symbols: tickers,
        category: 'all',
        key: 'all',
        ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
      };

      const api = isAuthenticated ? userTickerApi : tickerApi;
      const response = await api.getLatestAnalystViews(request);
      
      // Fetch ticker basics to get nextEarningsDate
      try {
        const tickerBasicsResponse = await api.getTickerBasics(tickers);
        if (tickerBasicsResponse.success && tickerBasicsResponse.data) {
          const earningsDates: Record<string, Date | null> = {};
          tickerBasicsResponse.data.forEach((tickerBasic: any) => {
            if (tickerBasic.nextEarningsDate) {
              const date = typeof tickerBasic.nextEarningsDate === 'string' 
                ? new Date(tickerBasic.nextEarningsDate) 
                : tickerBasic.nextEarningsDate;
              if (date instanceof Date && !isNaN(date.getTime())) {
                earningsDates[tickerBasic.tickerSymbol] = date;
              }
            }
          });
          setNextEarningsDates(prev => ({ ...prev, ...earningsDates }));
        }
      } catch (err) {
        console.error('Error fetching ticker basics for next earnings date:', err);
      }
      
      if (response.success) {
        const transformedData = transformAnalystViewsData(response.data);
        
        // Merge new data with existing data instead of replacing it
        setAnalystViewsData(prevData => {
          const existingData = prevData || [];
          const newDataMap = new Map(transformedData.map(item => [item.tickerSymbol, item]));
          
          // Update existing data with new data, or add new data
          const mergedData = existingData.map(existingItem => {
            const newItem = newDataMap.get(existingItem.tickerSymbol);
            return newItem || existingItem;
          });
          
          // Add any completely new tickers
          transformedData.forEach(newItem => {
            if (!existingData.some(existing => existing.tickerSymbol === newItem.tickerSymbol)) {
              mergedData.push(newItem);
            }
          });
          
          return mergedData;
        });

        // Safety: if analyst views data was fetched successfully, the ticker should not remain
        // in syncing/unsynced pulse state (especially when switching tabs mid-sync).
        setSyncingTickers(prev => {
          const next = new Set(prev);
          tickers.forEach((t: string) => next.delete(t));
          return next;
        });
        setUnsyncedTickers(prev => {
          const next = new Set(prev);
          tickers.forEach((t: string) => next.delete(t));
          return next;
        });
        
        // Note: lastUpdated should be set from last_sync_ended_at in checkSyncAndFetchDataForTickers
        console.log(`ListAnalystViews: Successfully fetched analyst views data for ${tickers.join(', ')}`);
      } else {
        setError(response.message || 'Failed to fetch analyst views');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching analyst views:', err);
    } finally {
      setLoading(false);
    }
  };

  

  // Fetch data for a single newly synced ticker and update local state
  const fetchDataForNewTicker = async (tickerSymbol: string, tickerName?: string) => {
    try {
      console.log(`ListAnalystViews: Fetching data for newly synced ticker: ${tickerSymbol}`);
      
      // Store ticker name
      if (tickerName) {
        setTickerNames(prev => ({ ...prev, [tickerSymbol]: tickerName }));
      }
      
      // Fetch analyst views data for this ticker only
      const analystViewsRequest = {
        ticker_symbols: [tickerSymbol],
        category: 'all',
        key: 'all'
      };

      const api = isAuthenticated ? userTickerApi : tickerApi;
      const analystViewsResponse = await api.getLatestAnalystViews(analystViewsRequest);
      
      // Fetch ticker basics to get nextEarningsDate
      try {
        const tickerBasicsResponse = await api.getTickerBasics([tickerSymbol]);
        if (tickerBasicsResponse.success && tickerBasicsResponse.data) {
          const tickerBasic = tickerBasicsResponse.data.find((tb: any) => tb.tickerSymbol === tickerSymbol.toUpperCase());
          if (tickerBasic && tickerBasic.nextEarningsDate) {
            const date = typeof tickerBasic.nextEarningsDate === 'string' 
              ? new Date(tickerBasic.nextEarningsDate) 
              : tickerBasic.nextEarningsDate;
            if (date instanceof Date && !isNaN(date.getTime())) {
              setNextEarningsDates(prev => ({ ...prev, [tickerSymbol]: date }));
            }
          }
        }
      } catch (err) {
        console.error('Error fetching ticker basics for next earnings date:', err);
      }
      
      // Fetch price data for this ticker only
      const priceResponse = await api.getTickerLatestPrice({
        ticker_symbols: [tickerSymbol],
      });

      // Process analyst views data
      if (analystViewsResponse.success && analystViewsResponse.data && analystViewsResponse.data.length > 0) {
        console.log(`ListAnalystViews: Successfully fetched analyst views data for ${tickerSymbol}`);
        
        // Transform the data using the shared transformation function
        const transformedData = transformAnalystViewsData(analystViewsResponse.data);

        // Update the analyst views data state with the new ticker data
        if (transformedData.length > 0) {
          setAnalystViewsData(prev => {
            const updated = [...prev];
            const existingIndex = updated.findIndex(item => item.tickerSymbol === tickerSymbol);
            if (existingIndex >= 0) {
              updated[existingIndex] = transformedData[0];
            } else {
              updated.push(transformedData[0]);
            }
            return updated;
          });
        }
      }

      // Process price data
      if (priceResponse.success && priceResponse.data && priceResponse.data.tickers) {
        console.log(`ListAnalystViews: Successfully fetched price data for ${tickerSymbol}`);
        
        const priceData = priceResponse.data.tickers.find((p: any) => p.ticker_symbol === tickerSymbol);
        if (priceData && priceData.prices && priceData.prices.length > 0) {
          const latestPrice = priceData.prices[0];
          const previousPrice = priceData.prices.length > 1 ? priceData.prices[1] : null;
          
          setPriceData(prev => ({
            ...prev,
            [tickerSymbol]: {
              ticker_symbol: priceData.ticker_symbol,
              ticker_basics_id: priceData.ticker_basics_id,
              close_price_adjusted: latestPrice.close_price_adjusted,
              date: new Date(latestPrice.date),
              previous_close_price_adjusted: previousPrice ? previousPrice.close_price_adjusted : null,
              previous_date: previousPrice ? new Date(previousPrice.date) : null,
              // Also add the format expected by transformDataForDisplay
              currentPrice: latestPrice.close_price_adjusted,
              previousPrice: previousPrice ? previousPrice.close_price_adjusted : null,
              change: latestPrice.change,
              changePercent: latestPrice.changePercent,
              volume: latestPrice.volume,
              high: latestPrice.high,
              low: latestPrice.low,
              open: latestPrice.open,
              prevClose: latestPrice.previousClose,
            }
          }));
        }
      }

      console.log(`ListAnalystViews: Successfully completed data fetch for ${tickerSymbol}`);
    } catch (error) {
      console.error(`ListAnalystViews: Error fetching data for ${tickerSymbol}:`, error);
    }
  };

  // Fetch analyst views data for a specific ticker (similar to ListTechnicals approach)
  const fetchAnalystViewsForTicker = async (tickerInfo: any) => {
    try {
      console.log(`ListAnalystViews: Fetching analyst views data for ${tickerInfo.ticker_symbol}`);
      
      // Store ticker name
      if (tickerInfo.ticker_name) {
        setTickerNames(prev => ({ ...prev, [tickerInfo.ticker_symbol]: tickerInfo.ticker_name }));
      }
      
      const request = {
        ticker_symbols: [tickerInfo.ticker_symbol],
        category: 'all',
        key: 'all',
        ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
      };

      const api = isAuthenticated ? userTickerApi : tickerApi;
      const response = await api.getLatestAnalystViews(request);
      
      if (response.success && response.data && response.data.length > 0) {
        console.log(`ListAnalystViews: Successfully fetched analyst views data for ${tickerInfo.ticker_symbol}`);
        
        // Transform the data using the same logic as useAnalystViews
        const transformedData = response.data.map(tickerData => {
          const displayData: any = {
            tickerSymbol: tickerData.tickerSymbol,
          };

          // Process analyst views by category
          const viewsByCategory = tickerData.analystViews.reduce((acc: any, view: any) => {
            if (!acc[view.category]) {
              acc[view.category] = {};
            }
            acc[view.category][view.key] = view.value;
            return acc;
          }, {} as Record<string, Record<string, number>>);

          // Price Targets
          const priceTargets = viewsByCategory.price_targets || {};
          if (priceTargets.targetMeanPrice) {
            displayData.averagePriceTarget = priceTargets.targetMeanPrice;
          }
          if (priceTargets.targetHighPrice && priceTargets.targetLowPrice) {
            displayData.priceTargetRange = `$${priceTargets.targetLowPrice.toFixed(2)} - $${priceTargets.targetHighPrice.toFixed(2)}`;
          }

          // Recommendations
          const recommendations = viewsByCategory.recommendations || {};
          const totalRecommendations = (recommendations.strongBuy || 0) + (recommendations.buy || 0) + 
                                     (recommendations.hold || 0) + (recommendations.sell || 0) + 
                                     (recommendations.strongSell || 0);
          
          if (totalRecommendations > 0) {
            displayData.strongBuyCount = recommendations.strongBuy || 0;
            displayData.buyCount = recommendations.buy || 0;
            displayData.holdCount = recommendations.hold || 0;
            displayData.sellCount = recommendations.sell || 0;
            displayData.strongSellCount = recommendations.strongSell || 0;
            
            // Calculate percentages
            displayData.strongBuyPercent = ((recommendations.strongBuy || 0) / totalRecommendations) * 100;
            displayData.buyPercent = ((recommendations.buy || 0) / totalRecommendations) * 100;
            displayData.holdPercent = ((recommendations.hold || 0) / totalRecommendations) * 100;
            displayData.sellPercent = ((recommendations.sell || 0) / totalRecommendations) * 100;
            displayData.strongSellPercent = ((recommendations.strongSell || 0) / totalRecommendations) * 100;
            
            // Calculate average recommendation
            const avgScore = ((recommendations.strongBuy || 0) * 5 + (recommendations.buy || 0) * 4 + 
                            (recommendations.hold || 0) * 3 + (recommendations.sell || 0) * 2 + 
                            (recommendations.strongSell || 0) * 1) / totalRecommendations;
            
            let recommendationText = '';
            if (avgScore >= 4.5) recommendationText = 'Strong Buy';
            else if (avgScore >= 3.5) recommendationText = 'Buy';
            else if (avgScore >= 2.5) recommendationText = 'Hold';
            else if (avgScore >= 1.5) recommendationText = 'Sell';
            else recommendationText = 'Strong Sell';
            
            displayData.averageRecommendation = `${recommendationText} (${avgScore.toFixed(1)})`;
          }

          // Earnings
          const earnings = viewsByCategory.earnings || {};
          
          // This Quarter (0q)
          if (earnings['0q_eps_avg']) {
            displayData.thisQuarterEpsAvg = earnings['0q_eps_avg'];
            if (earnings['0q_eps_high'] && earnings['0q_eps_low']) {
              displayData.thisQuarterEpsRange = `$${earnings['0q_eps_low'].toFixed(2)} - $${earnings['0q_eps_high'].toFixed(2)}`;
            }
          }
          if (earnings['0q_eps_yearAgoEps']) {
            displayData.thisQuarterEpsYearAgo = earnings['0q_eps_yearAgoEps'];
          }
          
          // Next Quarter (+1q)
          if (earnings['+1q_eps_avg']) {
            displayData.nextQuarterEpsAvg = earnings['+1q_eps_avg'];
            if (earnings['+1q_eps_high'] && earnings['+1q_eps_low']) {
              displayData.nextQuarterEpsRange = `$${earnings['+1q_eps_low'].toFixed(2)} - $${earnings['+1q_eps_high'].toFixed(2)}`;
            }
          }
          if (earnings['+1q_eps_yearAgoEps']) {
            displayData.nextQuarterEpsYearAgo = earnings['+1q_eps_yearAgoEps'];
          }
          
          // This Year (0y)
          if (earnings['0y_eps_avg']) {
            displayData.thisYearEpsAvg = earnings['0y_eps_avg'];
            if (earnings['0y_eps_high'] && earnings['0y_eps_low']) {
              displayData.thisYearEpsRange = `$${earnings['0y_eps_low'].toFixed(2)} - $${earnings['0y_eps_high'].toFixed(2)}`;
            }
          }
          if (earnings['0y_eps_yearAgoEps']) {
            displayData.thisYearEpsYearAgo = earnings['0y_eps_yearAgoEps'];
          }
          
          // Next Year (+1y)
          if (earnings['+1y_eps_avg']) {
            displayData.nextYearEpsAvg = earnings['+1y_eps_avg'];
            if (earnings['+1y_eps_high'] && earnings['+1y_eps_low']) {
              displayData.nextYearEpsRange = `$${earnings['+1y_eps_low'].toFixed(2)} - $${earnings['+1y_eps_high'].toFixed(2)}`;
            }
          }
          if (earnings['+1y_eps_yearAgoEps']) {
            displayData.nextYearEpsYearAgo = earnings['+1y_eps_yearAgoEps'];
          }

          return displayData;
        });

      } else {
        console.warn(`ListAnalystViews: No analyst views data found for ${tickerInfo.ticker_symbol}`);
      }
    } catch (error) {
      console.error(`ListAnalystViews: Error fetching analyst views data for ${tickerInfo.ticker_symbol}:`, error);
    }
  };
  
  // Price data state
  const [priceData, setPriceData] = useState<Record<string, any>>({});
  
  // Ticker names state
  const [tickerNames, setTickerNames] = useState<Record<string, string>>({});
  
  // Next earnings date state
  const [nextEarningsDates, setNextEarningsDates] = useState<Record<string, Date | null>>({});

  // Function to fetch price data for a list of tickers
  const fetchPriceData = async (tickers: string[]) => {
    if (tickers.length === 0) return;

    try {
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const response = await api.getTickerLatestPrice({
        ticker_symbols: tickers,
      });

      // Update price data state
      setPriceData(prev => {
        const updatedPriceData = { ...prev };
        
        if (response.data && response.data.tickers) {
          response.data.tickers.forEach((priceData: any) => {
            const symbol = priceData.ticker_symbol;
            if (priceData.prices && priceData.prices.length > 0) {
              // Use the first (most recent) price from the array
              const latestPrice = priceData.prices[0];
              const previousPrice = priceData.prices.length > 1 ? priceData.prices[1] : null;
              
              updatedPriceData[symbol] = {
                ticker_symbol: priceData.ticker_symbol,
                ticker_basics_id: priceData.ticker_basics_id,
                close_price_adjusted: latestPrice.close_price_adjusted,
                date: new Date(latestPrice.date),
                previous_close_price_adjusted: previousPrice ? previousPrice.close_price_adjusted : null,
                previous_date: previousPrice ? new Date(previousPrice.date) : null,
                // Also add the format expected by transformDataForDisplay
                currentPrice: latestPrice.close_price_adjusted,
                previousPrice: previousPrice ? previousPrice.close_price_adjusted : null,
                change: latestPrice.change,
                changePercent: latestPrice.changePercent,
                volume: latestPrice.volume,
                high: latestPrice.high,
                low: latestPrice.low,
                open: latestPrice.open,
                prevClose: latestPrice.previousClose,
              };
            }
          });
        }
        
        return updatedPriceData;
      });
    } catch (error) {
      console.error('Error fetching price data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Check if it's a rate limit error (429)
      if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
        handleRateLimitError();
      }
    }
  };

  // Function to clear interval without setting stopped flag (for cleanup/reset)
  const clearPollingInterval = () => {
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }
  };

  // Stop polling
  const stopPolling = () => {
    pollingStoppedRef.current = true;
    clearPollingInterval();
  };

  // Centralized function to handle 429 errors
  const handleRateLimitError = () => {
    console.log('Rate limit (429) detected, stopping all polling and API calls immediately');
    // Note: Error state is managed by useAnalystViews hook
    
    // Stop all polling using ref-based function
    stopPolling();
    
    // Clear all sync states
    setSyncingTickers(new Set());
    setUnsyncedTickers(new Set());
    setPollingLimitReached(new Set());
  };

  // Function to check sync status and fetch data for specific tickers
  const checkSyncAndFetchDataForTickers = async (tickersToCheck: string[], attemptNumber: number = 1) => {
    console.log('ListAnalystViews: checkSyncAndFetchDataForTickers called with:', tickersToCheck, `(attempt ${attemptNumber})`);
    
    if (tickersToCheck.length === 0) {
      console.log('ListAnalystViews: No tickers to check, returning early');
      return;
    }

    // Guard: Check if we've exceeded the attempt limit before proceeding
    if (attemptNumber > 5) {
      console.warn(`ListAnalystViews: Attempt ${attemptNumber} exceeds limit (5), stopping`);
      stopPolling();
      setPollingLimitReached(prev => {
        const newSet = new Set(prev);
        tickersToCheck.forEach((ticker: string) => newSet.add(ticker));
        return newSet;
      });
      
      // Remove these tickers from unsynced set
      setUnsyncedTickers(prev => {
        const newSet = new Set(prev);
        tickersToCheck.forEach((ticker: string) => newSet.delete(ticker));
        return newSet;
      });
      
      isCheckingSyncRef.current = false;
      return;
    }

    // Guard: Don't proceed if polling has been stopped (e.g., after reaching limit or 429 error)
    if (pollingStoppedRef.current) {
      console.log(`ListAnalystViews: checkSyncAndFetchDataForTickers called but polling is stopped, ignoring`);
      isCheckingSyncRef.current = false;
      return;
    }

    // Guard: Prevent concurrent execution - if a sync check is already in progress, ignore this call
    if (isCheckingSyncRef.current) {
      console.log(`ListAnalystViews: checkSyncAndFetchDataForTickers already in progress, ignoring concurrent call`);
      return;
    }

    isCheckingSyncRef.current = true;
      
    try {
      console.log('🚀 ListAnalystViews: About to call checkTickerSyncStatus for:', tickersToCheck);
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const syncResponse = await api.checkTickerSyncStatus({
        ticker_symbols: tickersToCheck,
      });
      console.log('🚀 ListAnalystViews: API call completed successfully for:', tickersToCheck);

      if (syncResponse.success && syncResponse.data && syncResponse.data.tickers) {
        // Calculate last updated from latest last_sync_ended_at of synced tickers
        const syncedTickers = syncResponse.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
        if (syncedTickers.length > 0) {
          const lastSyncDates = syncedTickers
            .map((t: any) => {
              const date = typeof t.last_sync_ended_at === 'string' 
                ? new Date(t.last_sync_ended_at) 
                : t.last_sync_ended_at;
              return date instanceof Date && !isNaN(date.getTime()) ? date : null;
            })
            .filter((d: Date | null): d is Date => d !== null);
          
          if (lastSyncDates.length > 0) {
            const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
            setLastUpdated(latestDate);
          }
        }
        
        // Store ticker names from sync response
        const newTickerNames: { [symbol: string]: string } = {};
        syncResponse.data.tickers.forEach((ticker: any) => {
          if (ticker.ticker_name) {
            newTickerNames[ticker.ticker_symbol] = ticker.ticker_name;
          }
        });
        setTickerNames(prev => ({ ...prev, ...newTickerNames }));

        const newlySyncedTickers = syncResponse.data.tickers
          .filter((ticker: any) => ticker.is_synced === true)
          .map((ticker: any) => ticker.ticker_symbol);
        
        const stillUnsyncedTickers = syncResponse.data.tickers
          .filter((ticker: any) => ticker.is_synced === false)
          .map((ticker: any) => ticker.ticker_symbol);

        console.log('🚀 ListAnalystViews: newlySyncedTickers:', newlySyncedTickers);
        console.log('🚀 ListAnalystViews: stillUnsyncedTickers:', stillUnsyncedTickers);

        // Update unsynced tickers state
        setUnsyncedTickers(prev => {
          const newSet = new Set(prev);
          stillUnsyncedTickers.forEach((ticker: string) => newSet.add(ticker));
          // Remove newly synced tickers from unsynced set
          newlySyncedTickers.forEach((ticker: string) => newSet.delete(ticker));
          return newSet;
        });

        // Remove newly synced tickers from syncing set so they don't show loading animation
        if (newlySyncedTickers.length > 0) {
          setSyncingTickers(prev => {
            const newSet = new Set(prev);
            newlySyncedTickers.forEach((ticker: string) => newSet.delete(ticker));
            return newSet;
          });
          
          // Save newly synced tickers to database if authenticated
          // Always save when tickers become synced, even if they're already in tickerList,
          // because the database mappings need to be updated now that they have ticker_basics_id
          if (isAuthenticated && currentListId !== null && newlySyncedTickers.length > 0) {
            try {
              const mergedList = Array.from(new Set([...newlySyncedTickers, ...tickerList]));
              await autoSaveList(mergedList);
              console.log(`💾 Saved ${newlySyncedTickers.length} newly synced tickers to database during polling:`, newlySyncedTickers);
            } catch (error) {
              console.error('❌ Error saving newly synced tickers to database:', error);
            }
          }
        }

        // Handle newly synced tickers only (tickers that are synced but don't have data yet)
        const tickersNeedingData = syncResponse.data.tickers
          .filter((ticker: any) => ticker.is_synced === true)
          .map((ticker: any) => ticker.ticker_symbol)
          .filter((ticker: string) => !analystViewsData.some(data => data.tickerSymbol === ticker));
        
        if (tickersNeedingData.length > 0) {
          console.log(`ListAnalystViews: Tickers needing data: ${tickersNeedingData.join(', ')}`);
          
          // Fetch both analyst views and price data for newly synced tickers
          console.log('ListAnalystViews: Fetching data for tickers without data');
          fetchAnalystViewsData(tickersNeedingData, syncResponse.data);
          fetchPriceData(tickersNeedingData);
        }

        // If there are still unsynced tickers, continue polling
        if (stillUnsyncedTickers.length > 0) {
          // If we've reached the polling limit, stop polling
          if (attemptNumber >= 5) {
            console.warn(`Sync polling limit reached (5 attempts) for tickers: ${stillUnsyncedTickers.join(', ')}. Stopping polling silently.`);
            setPollingLimitReached(prev => {
              const newSet = new Set(prev);
              stillUnsyncedTickers.forEach((ticker: string) => newSet.add(ticker));
              return newSet;
            });
            stopPolling();
            isCheckingSyncRef.current = false; // Reset flag
          } else {
            // Double-check we haven't stopped polling before setting up interval
            if (pollingStoppedRef.current) {
              console.log(`ListAnalystViews: Polling stopped, not setting up interval`);
              isCheckingSyncRef.current = false; // Reset flag before returning
              return;
            }

            console.log(`Found ${stillUnsyncedTickers.length} still unsynced tickers: ${stillUnsyncedTickers.join(', ')}. Attempt ${attemptNumber}/5.`);
            
            // Use fixed 2-second delay between polling attempts
            const pollingDelay = 2000; // 2 seconds
            
            // Clear any existing interval before creating a new one
            // This ensures we always use the latest attempt number
            clearPollingInterval();
            
            console.log(`Starting sync polling with ${pollingDelay}ms delay`);
            const interval = setInterval(() => {
              // Check if polling was stopped or we've reached the limit
              if (pollingStoppedRef.current) {
                clearInterval(interval);
                syncPollingIntervalRef.current = null;
                return;
              }
              
              // Get the next attempt number from the ref
              const nextAttempt = currentAttemptRef.current + 1;
              
              // Check if we've exceeded the limit
              if (nextAttempt > 5) {
                console.log(`ListAnalystViews: Reached attempt limit (5), stopping interval`);
                clearInterval(interval);
                syncPollingIntervalRef.current = null;
                pollingStoppedRef.current = true;
                return;
              }
              
              // Update the ref for the next iteration
              currentAttemptRef.current = nextAttempt;
              
              // Check current unsynced tickers from state
              setUnsyncedTickers(currentUnsynced => {
                if (currentUnsynced.size > 0) {
                  console.log(`Polling: checking ${currentUnsynced.size} unsynced tickers:`, Array.from(currentUnsynced));
                  checkSyncAndFetchDataForTickers(Array.from(currentUnsynced), nextAttempt);
                } else {
                  console.log('Polling: no unsynced tickers found, stopping polling');
                  clearInterval(interval);
                  syncPollingIntervalRef.current = null;
                  pollingStoppedRef.current = true;
                }
                return currentUnsynced;
              });
            }, pollingDelay);
            
            // Update the current attempt ref so the interval callback knows the next attempt number
            currentAttemptRef.current = attemptNumber;
            syncPollingIntervalRef.current = interval;
            
            // Reset flag after setting up interval
            isCheckingSyncRef.current = false;
          }
        } else {
          // All tickers synced, stop polling
          console.log('All tickers synced, stopping polling');
          stopPolling();
          isCheckingSyncRef.current = false; // Reset flag
        }
      }
      setError(null);
      isCheckingSyncRef.current = false; // Reset flag on success
    } catch (err) {
      console.error('ListAnalystViews: Error in checkSyncAndFetchDataForTickers:', err);
      
      // Check if it's a 429 rate limit error
      const isRateLimitError = 
        (axios.isAxiosError(err) && err.response?.status === 429) ||
        (err instanceof Error && (err.message.includes('429') || err.message.includes('rate limit') || err.message.includes('Rate limit')));
      
      if (isRateLimitError) {
        console.warn(`ListAnalystViews: Rate limit (429) detected. Stopping polling.`);
        handleRateLimitError();
      } else {
        // Stop polling on any error
        stopPolling();
      }
      isCheckingSyncRef.current = false; // Reset flag
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (tickerList.length === 0) return;
    
    // Reset polling state on initial mount or when dependencies change
    pollingStoppedRef.current = false;
    currentAttemptRef.current = 1;
    isCheckingSyncRef.current = false;
    clearPollingInterval();
    
    const checkInitialSyncStatus = async () => {
      try {
        console.log('ListAnalystViews: checkInitialSyncStatus called');
        const api = isAuthenticated ? userTickerApi : tickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: tickerList,
        });
        console.log('ListAnalystViews: Initial sync check response:', syncResponse);

        if (syncResponse.success && syncResponse.data && syncResponse.data.tickers) {
          // Calculate last updated from latest last_sync_ended_at of synced tickers
          const syncedTickersWithDate = syncResponse.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
          if (syncedTickersWithDate.length > 0) {
            const lastSyncDates = syncedTickersWithDate
              .map((t: any) => {
                const date = typeof t.last_sync_ended_at === 'string' 
                  ? new Date(t.last_sync_ended_at) 
                  : t.last_sync_ended_at;
                return date instanceof Date && !isNaN(date.getTime()) ? date : null;
              })
              .filter((d: Date | null): d is Date => d !== null);
            
            if (lastSyncDates.length > 0) {
              const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
              setLastUpdated(latestDate);
            }
          }

          // Store ticker names from sync response
          const newTickerNames: { [symbol: string]: string } = {};
          syncResponse.data.tickers.forEach((ticker: any) => {
            if (ticker.ticker_name) {
              newTickerNames[ticker.ticker_symbol] = ticker.ticker_name;
            }
          });
          setTickerNames(prev => ({ ...prev, ...newTickerNames }));

          const syncedTickers = syncResponse.data.tickers
            .filter((ticker: any) => ticker.is_synced === true)
            .map((ticker: any) => ticker.ticker_symbol);
          
          const unsyncedTickers = syncResponse.data.tickers
            .filter((ticker: any) => ticker.is_synced === false)
            .map((ticker: any) => ticker.ticker_symbol);

          console.log('ListAnalystViews: syncedTickers:', syncedTickers);
          console.log('ListAnalystViews: unsyncedTickers:', unsyncedTickers);

          // Update unsynced tickers state
          setUnsyncedTickers(new Set(unsyncedTickers));

          // Fetch data for all synced tickers
          if (syncedTickers.length > 0) {
            console.log(`ListAnalystViews: Found synced tickers: ${syncedTickers.join(', ')}`);
            console.log(`ListAnalystViews: Fetching data for all synced tickers: ${syncedTickers.join(', ')}`);
            fetchAnalystViewsData(syncedTickers, syncResponse.data);
            fetchPriceData(syncedTickers);
          }

          // If there are unsynced tickers, start polling for them immediately
          if (unsyncedTickers.length > 0) {
            console.log(`ListAnalystViews: Found unsynced tickers: ${unsyncedTickers.join(', ')}`);
            console.log('ListAnalystViews: Starting immediate check for unsynced tickers');
            
            // Reset polling state when starting new polling
            pollingStoppedRef.current = false;
            currentAttemptRef.current = 1; // Reset attempt counter
            isCheckingSyncRef.current = false; // Reset concurrent check flag
            
            // Only start polling if we don't already have an active polling interval
            if (!syncPollingIntervalRef.current) {
              setTimeout(() => {
                console.log('ListAnalystViews: Executing immediate check for unsynced tickers');
                checkSyncAndFetchDataForTickers(unsyncedTickers, 1);
              }, 1000); // 1 second delay to avoid rate limiting
            } else {
              console.log('ListAnalystViews: Polling already active, skipping new polling start');
            }
          }
        }
      } catch (error) {
        console.error('Error in initial sync check:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        
        // Check if it's a rate limit error (429)
        if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
          handleRateLimitError();
        }
      }
    };

    checkInitialSyncStatus();

    return () => {
      stopPolling();
    };
  }, [tickerList.join(',')]);

  // Register current state with the global session save service
  useEffect(() => {
    if (!isAuthenticated) {
      sessionSaveService.registerSessionData({
        tickerList,
        listName,
        hiddenStatistics: {
          analystViews: hiddenStatistics
        }
      });
    }
  }, [tickerList, listName, hiddenStatistics, isAuthenticated]);

  // Persist syncingTickers to sessionStorage whenever it changes
  // Use list ID-specific key for authenticated users
  // Also migrate from global key to list ID-specific key if currentListId becomes available
  useEffect(() => {
    if (!isAuthenticated || currentListId === null) {
      // For unauthenticated users, use global key
      if (syncingTickers.size > 0) {
        sessionStorage.setItem('stocktable-syncing-tickers', JSON.stringify(Array.from(syncingTickers)));
      } else {
        sessionStorage.removeItem('stocktable-syncing-tickers');
      }
      return;
    }
    
    // For authenticated users, use list ID-specific key
    const key = getSyncingTickersKey();
    
    // Migrate from global key to list ID-specific key on first use
    const globalKey = 'stocktable-syncing-tickers';
    const globalSaved = sessionStorage.getItem(globalKey);
    if (globalSaved && syncingTickers.size === 0) {
      // If we have syncing tickers in global key but not in state, restore them
      try {
        const globalTickers = JSON.parse(globalSaved);
        setSyncingTickers(new Set(globalTickers));
        // Clear global key after migration
        sessionStorage.removeItem(globalKey);
      } catch (error) {
        console.error('Error migrating syncing tickers:', error);
      }
    }
    
    if (syncingTickers.size > 0) {
      sessionStorage.setItem(key, JSON.stringify(Array.from(syncingTickers)));
    } else {
      sessionStorage.removeItem(key);
    }
  }, [syncingTickers, getSyncingTickersKey, isAuthenticated, currentListId]);

  // Restore syncing state for tickers that were syncing before refresh (only on initial load)
  useEffect(() => {
    // Only run once on initial load, not when new tickers are added during the session
    if (hasRestoredSyncingStateRef.current) return;
    
    // Wait for tickerList to be loaded
    if (tickerList.length === 0) return;
    
    const restoreSyncingState = async () => {
      // Double-check flag to prevent race conditions
      if (hasRestoredSyncingStateRef.current) return;
      
      // Get tickers that were syncing from sessionStorage and are in current list
      const syncingArray = Array.from(syncingTickers).filter(ticker => tickerList.includes(ticker));
      if (syncingArray.length === 0) {
        // Mark as restored even if no tickers to restore
        hasRestoredSyncingStateRef.current = true;
        return;
      }
      
      // Mark as restored before making the API call to prevent duplicate calls
      hasRestoredSyncingStateRef.current = true;
      
      try {
        console.log(`🔄 Restoring sync status for ${syncingArray.length} tickers that were syncing before refresh:`, syncingArray);
        const api = isAuthenticated ? userTickerApi : tickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: syncingArray,
        });
        
        const syncedTickers: string[] = [];
        const stillSyncingTickers: string[] = [];
        
        syncResponse.data?.tickers?.forEach((t: any) => {
          if (t.is_synced) {
            syncedTickers.push(t.ticker_symbol);
          } else {
            stillSyncingTickers.push(t.ticker_symbol);
          }
        });
        
        // Remove synced tickers from syncing set and unsynced set, then fetch their data
        if (syncedTickers.length > 0) {
          setSyncingTickers(prev => {
            const newSet = new Set(prev);
            syncedTickers.forEach(ticker => newSet.delete(ticker));
            return newSet;
          });
          // Also remove from unsyncedTickers to ensure they're not shown as loading
          setUnsyncedTickers(prev => {
            const newSet = new Set(prev);
            syncedTickers.forEach(ticker => newSet.delete(ticker));
            return newSet;
          });
          console.log(`✅ ${syncedTickers.length} tickers finished syncing:`, syncedTickers);
          
          // Fetch data for synced tickers
          console.log(`🔄 Fetching data for ${syncedTickers.length} restored synced tickers:`, syncedTickers);
          setTimeout(async () => {
            try {
              // Fetch analyst views and price data for synced tickers
              await fetchAnalystViewsData(syncedTickers, syncResponse.data);
              await fetchPriceData(syncedTickers);
              
              // Save synced tickers to database if authenticated
              if (isAuthenticated && currentListId !== null && syncedTickers.length > 0) {
                const mergedList = Array.from(new Set([...syncedTickers, ...tickerList]));
                
                // Only save if the merged list is actually different from current tickerList
                // This prevents unnecessary API calls when tickers are already in the list
                const currentListSet = new Set(tickerList);
                const isDifferent = mergedList.length !== tickerList.length || 
                                   !syncedTickers.every((ticker: string) => currentListSet.has(ticker));
                
                if (isDifferent) {
                  await autoSaveList(mergedList);
                  console.log(`💾 Saved ${syncedTickers.length} restored synced tickers to database:`, syncedTickers);
                } else {
                  console.log(`ℹ️ No changes needed - tickers are already in the list`);
                }
              }
              
              console.log(`✅ Successfully fetched data for restored synced tickers:`, syncedTickers);
            } catch (error) {
              console.error('❌ Error fetching data for restored synced tickers:', error);
            }
          }, 300); // Small delay to avoid rate limiting
        }
        
        // Keep still-syncing tickers in the set and add them to unsyncedTickers to resume polling
        if (stillSyncingTickers.length > 0) {
          console.log(`⏳ ${stillSyncingTickers.length} tickers still syncing, resuming polling:`, stillSyncingTickers);
          
          // Reset polling state to ensure polling can resume
          pollingStoppedRef.current = false;
          currentAttemptRef.current = 1;
          isCheckingSyncRef.current = false;
          
          // Add to unsyncedTickers to trigger polling
          setUnsyncedTickers(prev => {
            const newSet = new Set(prev);
            stillSyncingTickers.forEach(ticker => newSet.add(ticker));
            return newSet;
          });
          
          // Start polling for these tickers
          setTimeout(() => {
            console.log('🔄 Starting polling for restored syncing tickers:', stillSyncingTickers);
            checkSyncAndFetchDataForTickers(stillSyncingTickers, 1);
          }, 1000); // 1 second delay to avoid rate limiting
        }
      } catch (error) {
        console.error('❌ Error checking sync status for syncing tickers:', error);
      }
    };

    // Run once after tickerList is loaded (with a delay to ensure it's fully loaded)
    const timeoutId = setTimeout(restoreSyncingState, 500);
    return () => clearTimeout(timeoutId);
  }, [tickerList, syncingTickers, isAuthenticated, currentListId, setUnsyncedTickers, checkSyncAndFetchDataForTickers, autoSaveList]); // Only run when tickerList is first loaded

  // Recovery: Merge tickers from sessionStorage into tickerList immediately (for authenticated users)
  // This ensures tickers that were added but not yet in database appear immediately with loading state
  useEffect(() => {
    const mergeSessionStorageTickers = () => {
      if (!isAuthenticated || currentListId === null) return;
      
      // Check sessionStorage for tickers that might not be in database yet
      // Use list ID-specific key to only merge tickers for the current list
      const savedTickers = sessionStorage.getItem(getTickerListKey());
      if (!savedTickers) return;
      
      try {
        const sessionTickers: string[] = JSON.parse(savedTickers);
        // Deduplicate sessionStorage tickers first
        const uniqueSessionTickers = Array.from(new Set(sessionTickers));
        // Deduplicate current tickerList
        const uniqueTickerList = Array.from(new Set(tickerList));
        // Find tickers that are in sessionStorage but not in current list
        const missingTickers = uniqueSessionTickers.filter(ticker => !uniqueTickerList.includes(ticker));
        
        if (missingTickers.length === 0) {
          // If no missing tickers but sessionStorage has duplicates, clean it up
          if (uniqueSessionTickers.length !== sessionTickers.length) {
            sessionStorage.setItem(getTickerListKey(), JSON.stringify(uniqueSessionTickers));
            console.log(`🔧 Cleaned up duplicates in sessionStorage: ${sessionTickers.length} -> ${uniqueSessionTickers.length}`);
          }
          return;
        }
        
        console.log(`🔄 Found ${missingTickers.length} tickers in sessionStorage not in database list:`, missingTickers);
        
        // Preserve order from sessionStorage: merge by keeping sessionStorage order and inserting missing tickers
        // at their original positions, then appending any remaining tickers from database list
        const mergedList: string[] = [];
        
        // First, add all tickers from sessionStorage in their original order
        for (const ticker of uniqueSessionTickers) {
          if (!mergedList.includes(ticker)) {
            mergedList.push(ticker);
          }
        }
        
        // Then, add any tickers from database list that weren't in sessionStorage
        for (const ticker of uniqueTickerList) {
          if (!mergedList.includes(ticker)) {
            mergedList.push(ticker);
          }
        }
        
        setTickerList(mergedList);
        // Also save the deduplicated merged list back to sessionStorage
        sessionStorage.setItem(getTickerListKey(), JSON.stringify(mergedList));
        console.log(`✅ Merged ${missingTickers.length} tickers from sessionStorage into list, preserving order`);
        
        // Also restore syncing state for these tickers if they were syncing
        // Use list ID-specific key to only restore syncing state for the current list
        const savedSyncing = sessionStorage.getItem(getSyncingTickersKey());
        if (savedSyncing) {
          const syncingArray: string[] = JSON.parse(savedSyncing);
          const syncingTickersToRestore = syncingArray.filter(ticker => missingTickers.includes(ticker));
          if (syncingTickersToRestore.length > 0) {
            setSyncingTickers(prev => {
              const newSet = new Set(prev);
              syncingTickersToRestore.forEach(ticker => newSet.add(ticker));
              return newSet;
            });
            // Also add to unsyncedTickers so polling can resume
            setUnsyncedTickers(prev => {
              const newSet = new Set(prev);
              syncingTickersToRestore.forEach(ticker => newSet.add(ticker));
              return newSet;
            });
            console.log(`✅ Restored syncing state for ${syncingTickersToRestore.length} tickers:`, syncingTickersToRestore);
            
            // Start polling for these tickers immediately
            // Reset polling state to ensure polling can resume
            pollingStoppedRef.current = false;
            currentAttemptRef.current = 1;
            isCheckingSyncRef.current = false;
            
            // Start polling for these tickers
            setTimeout(() => {
              console.log('🔄 Starting polling for merged syncing tickers:', syncingTickersToRestore);
              checkSyncAndFetchDataForTickers(syncingTickersToRestore, 1);
            }, 1000); // 1 second delay to avoid rate limiting
          }
        }
      } catch (error) {
        console.error('❌ Error merging sessionStorage tickers:', error);
      }
    };

    // Run after tickerList is loaded from database (with a small delay to ensure database list is loaded)
    const timeoutId = setTimeout(mergeSessionStorageTickers, 100);
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, currentListId, tickerList, setTickerList, setSyncingTickers, setUnsyncedTickers, getTickerListKey, getSyncingTickersKey, checkSyncAndFetchDataForTickers]);

  // Sync all tickers
  const syncAllTickers = async () => {
    if (tickerList.length === 0) return;

    setIsSyncing(true);
    try {
      for (const ticker of tickerList) {
        try {
          await syncApiService.syncTicker(ticker, {
            syncProsAndCons: Boolean(isAuthenticated && user?.hasProSubscription),
          });
        } catch (error) {
          console.error(`Error syncing ${ticker}:`, error);
        }
      }
      // Refresh sync statuses after syncing
      setTimeout(() => {
        // Re-check sync status for all tickers
        const checkSyncAfterSync = async () => {
          try {
            const api = isAuthenticated ? userTickerApi : tickerApi;
            const syncResponse = await api.checkTickerSyncStatus({
              ticker_symbols: tickerList,
            });
            
            if (syncResponse.success && syncResponse.data && syncResponse.data.tickers) {
              const unsyncedTickers = syncResponse.data.tickers
                .filter((ticker: any) => ticker.is_synced === false)
                .map((ticker: any) => ticker.ticker_symbol);
              
              if (unsyncedTickers.length > 0) {
                // Reset polling state before starting new sync check
                pollingStoppedRef.current = false;
                currentAttemptRef.current = 1;
                isCheckingSyncRef.current = false;
                clearPollingInterval();
                
                checkSyncAndFetchDataForTickers(unsyncedTickers, 1);
              }
            }
          } catch (error) {
            console.error('Error checking sync status after sync:', error);
          }
        };
        
        checkSyncAfterSync();
      }, 1000);
    } catch (error) {
      console.error('Error syncing tickers:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Check if it's a rate limit error (429)
      if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
        handleRateLimitError();
      }
    } finally {
      setIsSyncing(false);
    }
  };


  const handleTickerClick = (tickerSymbol: string) => {
    if (isPublicList && publicUuid) {
      navigate(`/ticker/${tickerSymbol}/analysts?public_uuid=${publicUuid}`);
    } else {
      navigate(`/ticker/${tickerSymbol}/analysts`);
    }
  };

  // Handle remove ticker
  const handleRemoveTicker = async (ticker: string) => {
    const newTempList = tempTickerList.filter(t => t !== ticker);
    setTempTickerList(newTempList);
    
    // No auto-save in edit mode - will be saved when "Done" is clicked
    // Auto-save only happens when not in editing mode (which is rare for remove operations)
    if (isAuthenticated && !isEditingTickers) {
      const newList = tickerList.filter(t => t !== ticker);
      await autoSaveList(newList);
    }
  };


  // Handle statistic visibility toggle
  const handleStatisticToggle = (statisticKey: string) => {
    if (hiddenStatistics.includes(statisticKey)) {
      handleShowStatistic(statisticKey);
    } else {
      handleHideStatistic(statisticKey);
    }
  };

  // Transform data for display - create a hybrid type that includes both TickerDisplayData and AnalystViewDisplayData
  const transformDataForDisplay = (data: AnalystViewDisplayData[]): any[] => {
    return data.map(item => {
      const symbol = item.tickerSymbol;
      const currentPrice = priceData[symbol]?.currentPrice || priceData[symbol]?.close_price_adjusted;
      const previousPrice = priceData[symbol]?.previousPrice || priceData[symbol]?.previous_close_price_adjusted;
      const change = currentPrice && previousPrice ? currentPrice - previousPrice : (priceData[symbol]?.change || 0);
      const changePercent = currentPrice && previousPrice ? (change / previousPrice) * 100 : (priceData[symbol]?.changePercent || 0);
      
      // Calculate price target difference
      const priceTargetDifference = currentPrice && item.averagePriceTarget 
        ? ((item.averagePriceTarget - currentPrice) / currentPrice) * 100 
        : undefined;

      return {
      // Required TickerDisplayData fields
        ticker: symbol,
        name: tickerNames[symbol] || symbol,
        latest: currentPrice || 0,
        change: change,
        changePercent: changePercent,
      volume: 0,
      high: 0,
      low: 0,
      open: 0,
        prevClose: previousPrice || 0,
      // Add all analyst view data
      ...item,
        // Override with calculated values
        lastClosingPrice: currentPrice,
        priceTargetDifference: priceTargetDifference,
        // Add next earnings date
        nextEarningsDate: nextEarningsDates[symbol] || null,
        // Add ticker name for hover tooltip
        ticker_name: tickerNames[symbol] || symbol,
      };
    });
  };

  // Get ticker data for a specific symbol
  const getTickerData = (symbol: string) => {
    const isSyncingTicker = syncingTickers.has(symbol);
    const isUnsyncedTicker = unsyncedTickers.has(symbol);
    
    // Use local data
    const dataToUse = analystViewsData;
    const transformedData = transformDataForDisplay(dataToUse);
    const tickerData = transformedData.find(item => item.ticker === symbol);
    
    if (isSyncingTicker || isUnsyncedTicker || !tickerData) {
      return {
        ticker: symbol,
        name: symbol,
        ticker_name: tickerNames[symbol] || symbol,
        latest: 0,
        change: 0,
        changePercent: 0,
        volume: 0,
        high: 0,
        low: 0,
        open: 0,
        prevClose: 0,
        updated: Date.now(),
        isSyncing: isSyncingTicker || isUnsyncedTicker,
        // Add all analyst view data with default values
        averagePriceTarget: 0,
        priceTargetRange: '',
        lastClosingPrice: 0,
        priceTargetDifference: 0,
        averageRecommendation: '',
        strongBuyCount: 0,
        buyCount: 0,
        holdCount: 0,
        sellCount: 0,
        strongSellCount: 0,
        strongBuyPercent: 0,
        buyPercent: 0,
        holdPercent: 0,
        sellPercent: 0,
        strongSellPercent: 0,
        thisQuarterEpsAvg: 0,
        thisQuarterEpsRange: '',
        thisQuarterEpsYearAgo: 0,
        nextQuarterEpsAvg: 0,
        nextQuarterEpsRange: '',
        nextQuarterEpsYearAgo: 0,
        thisYearEpsAvg: 0,
        thisYearEpsRange: '',
        thisYearEpsYearAgo: 0,
        nextYearEpsAvg: 0,
        nextYearEpsRange: '',
        nextYearEpsYearAgo: 0,
        // Flag that analyst views are unavailable (e.g., ETF/ETV not returned by API)
        isAnalystViewsUnavailable: !tickerData && !isSyncingTicker && !isUnsyncedTicker,
      };
    }
    
    return {
      ...tickerData,
      isSyncing: isSyncingTicker,
    };
  };

  // Format statistic value
  const formatStatisticValue = (statisticKey: string, ticker: any) => {
    // Check if this statistic is locked for non-pro users BEFORE formatting
    // If the value is null and the statistic is locked, show lock icon
    if (lockedStatistics.includes(statisticKey)) {
      const value = ticker[statisticKey];
      // If value is null (locked), show lock icon instead of formatting
      if (value === null || value === undefined) {
        return <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? () => navigate('/pricing', { state: { fromUpgrade: true } }) : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
      }
    }
    
    return formatAnalystViewsStatisticValue(statisticKey, ticker, {
      syncingTickers,
      unsyncedTickers,
      pollingLimitReached,
    });
  };

  // Get statistic color
  const getStatisticColor = (statisticKey: string, ticker: any) => {
    return getAnalystViewsStatisticColor(statisticKey, ticker, {
      syncingTickers,
      unsyncedTickers,
      pollingLimitReached,
    });
  };


  // Recovery: Check for tickers in sessionStorage that need to be synced to database
  useEffect(() => {
    // Only run once per list load to prevent unnecessary API calls
    if (hasRecoveredPendingTickersRef.current) return;
    
    const recoverPendingTickers = async () => {
      if (!isAuthenticated || !currentListId || tickerList.length === 0) return;
      
      // Check sessionStorage for tickers that might not be in database yet
      // Use list ID-specific key to only recover tickers for the current list
      const savedTickers = sessionStorage.getItem(getTickerListKey());
      if (!savedTickers) {
        hasRecoveredPendingTickersRef.current = true;
        return;
      }
      
      try {
        const sessionTickers: string[] = JSON.parse(savedTickers);
        const missingTickers = sessionTickers.filter(ticker => !tickerList.includes(ticker));
        
        if (missingTickers.length === 0) {
          // No missing tickers - mark as recovered and clear sessionStorage if everything is already saved
          hasRecoveredPendingTickersRef.current = true;
          // Clear sessionStorage since everything is already in the database
          sessionStorage.removeItem(getTickerListKey());
          return;
        }
        
        console.log(`🔄 Checking sync status for ${missingTickers.length} tickers in sessionStorage:`, missingTickers);
        
        // Check if these tickers have been synced (exist in tickerBasics)
        const api = userTickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: missingTickers,
        });
        
        const syncedTickers = syncResponse.data?.tickers
          ?.filter((t: any) => t.is_synced === true)
          .map((t: any) => t.ticker_symbol) || [];
        
        if (syncedTickers.length === 0) {
          console.log('⚠️ No missing tickers are synced yet, will retry later');
          // Don't mark as recovered yet - we'll retry later
          return;
        }
        
        // Mark as recovered since we're processing the recovery now
        hasRecoveredPendingTickersRef.current = true;
        
        console.log(`✅ Found ${syncedTickers.length} synced tickers that need to be saved:`, syncedTickers);
        
        // Merge synced tickers with current list and save to database
        const mergedList = Array.from(new Set([...syncedTickers, ...tickerList]));
        
        // Only save if the merged list is actually different from current tickerList
        // This prevents unnecessary API calls on page refresh when nothing has changed
        const currentListSet = new Set(tickerList);
        const isDifferent = mergedList.length !== tickerList.length || 
                           !syncedTickers.every((ticker: string) => currentListSet.has(ticker));
        
        if (isDifferent) {
          await autoSaveList(mergedList);
          console.log(`💾 Recovered and saved ${syncedTickers.length} tickers to database`);
          // Update local state
          setTickerList(mergedList);
          // Clear sessionStorage after successful save
          sessionStorage.removeItem(getTickerListKey());
        } else {
          console.log(`ℹ️ No changes needed - all tickers are already in the list`);
          // Clear sessionStorage since everything is already saved
          sessionStorage.removeItem(getTickerListKey());
        }
      } catch (error) {
        console.error('❌ Error recovering pending tickers:', error);
        // Mark as recovered even on error to prevent infinite retries
        hasRecoveredPendingTickersRef.current = true;
      }
    };

    // Only run recovery once after initial load, with a delay to ensure database list is loaded
    const timeoutId = setTimeout(recoverPendingTickers, 1500);
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, currentListId, tickerList, autoSaveList, setTickerList, getTickerListKey]);
  
  // Reset recovery flag when list ID changes
  useEffect(() => {
    hasRecoveredPendingTickersRef.current = false;
  }, [currentListId]);

  // Handle add ticker using useAddTicker hook (defined after autoSaveList)
  const { handleAddTicker } = useAddTicker({
    isAuthenticated,
    tickerList,
    setTickerList,
    setSyncingTickers,
    tempTickerList: tempTickerList || [],
    setTempTickerList: setTempTickerList as React.Dispatch<React.SetStateAction<string[]>> | undefined,
    isEditingTickers,
    onTickerNameUpdate: useCallback((ticker: string, tickerName: string) => {
      if (tickerName) {
        setTickerNames(prev => ({
          ...prev,
          [ticker]: tickerName
        }));
      }
    }, []),
    onSyncComplete: useCallback(async (ticker: string, tickerStatus: any) => {
      // Remove from unsynced set
      setUnsyncedTickers(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticker);
        return newSet;
      });
      
      // Fetch data specifically for the newly synced ticker only
      await fetchDataForNewTicker(ticker, tickerStatus?.ticker_name);
    }, [fetchDataForNewTicker]),
    autoSaveList,
    saveListImmediately: useCallback(async (tickers: string[]) => {
      const uniqueTickers = Array.from(new Set(tickers));
      if (isPublicList) return;

      if (isAuthenticated && !isPublicList) {
        let listId = currentListId;
        if (listId === null) {
          const cachedListId = getCachedCurrentListId();
          if (cachedListId) listId = cachedListId;
          else if (userLists.length > 0) {
            try {
              const defaultListResponse = await userApi.getDefaultList();
              if (defaultListResponse.defaultUserTickerListId && userLists.some((l: UserTickerList) => l.id === defaultListResponse.defaultUserTickerListId)) {
                listId = defaultListResponse.defaultUserTickerListId;
              } else {
                listId = userLists[0].id;
              }
            } catch {
              listId = userLists[0].id;
            }
          }
        }
        if (listId != null) {
          try {
            const tickerMappings = uniqueTickers.map((ticker, index) => ({ ticker_symbol: ticker, order_num: index + 1 }));
            await userTickerListApi.updateTickerMappings(listId, tickerMappings);
            clearListCache(listId);
            needsListDataReload.current = true;
          } catch (error) {
            console.error('saveListImmediately: failed to save to server', error);
          }
        }
        return;
      }

      sessionStorage.setItem(getTickerListKey(), JSON.stringify(uniqueTickers));
    }, [getTickerListKey, currentListId, isPublicList, isAuthenticated, userLists]),
    pollingInterval: 2000,
    maxPollAttempts: 10,
    normalizeTicker: (ticker: string) => ticker.toUpperCase(),
    onError: useCallback((ticker: string) => {
      // Add to polling limit reached set on max attempts
      setPollingLimitReached(prev => {
        const newSet = new Set(prev);
        newSet.add(ticker);
        return newSet;
      });
    }, []),
  });



  // Handle refresh usage limits callback
  const handleRefreshUsageLimits = (refreshFn: () => void) => {
    refreshUsageLimitsRef.current = refreshFn;
  };

  // Share modal handlers
  const handleShareList = () => {
    setIsShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setIsShareModalOpen(false);
  };

  // Handle category info click
  const handleCategoryInfoClick = (category: string) => {
    setSelectedCategory(category);
    setIsCategoryModalOpen(true);
  };

  // Handle category modal close
  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setSelectedCategory('');
  };

  // Handle indicator info click
  const handleIndicatorInfoClick = (indicatorKey: string) => {
    setSelectedIndicatorKey(indicatorKey);
    setIsIndicatorModalOpen(true);
  };

  // Handle indicator modal close
  const handleCloseIndicatorModal = () => {
    setIsIndicatorModalOpen(false);
    setSelectedIndicatorKey('');
  };

  const handleShareModalUpdate = async () => {
    // Refresh user lists to get updated share settings
    if (isAuthenticated && currentListId) {
      try {
        const updatedLists = await userTickerListApi.getUserLists();
        setUserLists(updatedLists);
        const updatedList = updatedLists.find(list => list.id === currentListId);
        if (updatedList) {
          cacheUserLists(updatedLists);
        }
      } catch (error) {
        console.error('Failed to refresh lists after share update:', error);
      }
    }
  };

  // Get current list data for share modal
  const currentList = currentListId && userLists
    ? userLists.find(list => list.id === currentListId) || null
    : null;

  // Modals hook (must be after handleListChange is defined)
  const {
    isDeleteModalOpen,
    isErrorDialogOpen,
    errorMessage,
    handleDeleteList,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleErrorDialogClose,
  } = useModals({
    currentListId,
    isAuthenticated,
    userLists,
    needsUserListsReload,
    handleListChange,
    refreshUsageLimitsRef,
    onAfterDelete: () => {
      setCurrentListId(null);
      setListName('Main List');
      setTickerList([]);
      setHiddenStatistics([]);
    },
  });

  // Tabs component
  const tabs = (
    <TabBar 
      activeTab="analysts" 
      disabled={isEditingTickers} 
      publicUuid={publicUuid}
    />
  );

  // Stock Table now calculates category headers dynamically (like TickerTechnicals)
  // So we just pass the base category headers with original startIndices
  // Stock Table will calculate which headers to show based on current hiddenStatistics
  const currentCategoryHeaders = ANALYST_VIEWS_CATEGORY_HEADERS;

  // If public list is not found, show simple centered message
  // Also check publicUuid directly to ensure we exit this state when URL changes
  if (publicUuid && isPublicListNotFound) {
    return (
      <>
        <div className="flex flex-col min-h-screen">
          <Header />
          {!isAuthenticated && <PublicListPromoBar />}
          <div className="flex-grow flex items-center justify-center">
            <div className="text-center">
              <p className="text-lg text-gray-700 dark:text-text-primary">
                {publicListError || 'This list does not exist or is no longer available.'}
              </p>
            </div>
          </div>
          <div className="mt-4 sm:mt-8">
            <Footer />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
    <div className="flex flex-col min-h-screen">
      <StockTableLayout
        tickerList={displayTickerList}
        tickerData={transformDataForDisplay(analystViewsData)}
        loading={loading || isLoadingPublicList}
        error={error || (isPublicList ? publicListError : null)}
        lastUpdated={lastUpdated}
        isEditingTickers={isPublicList ? false : isEditingTickers}
        isPublicList={isPublicList}
        showPublicListPromoBar={isPublicList && !isAuthenticated}
        hiddenStatistics={hiddenStatistics}
        draggedTicker={draggedTicker}
        dragOverTicker={dragOverTicker}
        droppedTicker={droppedTicker}
        statistics={ANALYST_VIEWS_STATISTICS}
        categoryHeaders={currentCategoryHeaders}
        getTickerData={getTickerData}
        formatStatisticValue={formatStatisticValue}
        getStatisticColor={getStatisticColor}
        lockedStatistics={lockedStatistics}
        proOnlyStatistics={PRO_ONLY_STATISTICS}
        lockedTickers={lockedTickers}
        tickerColumnWidth="150px"
        statisticsColumnWidthMultiplier={0.8}
        useListDailySmallScreenColumnWidth
        onRefetch={() => fetchAnalystViewsData(tickerList)}
        onTickerClick={handleTickerClick}
        onRemoveTicker={isPublicList ? () => {} : handleRemoveTicker}
        onDragStart={isPublicList ? () => {} : handleDragStart}
        onDragOver={isPublicList ? () => {} : handleDragOver}
        onDragLeave={isPublicList ? () => {} : handleDragLeave}
        onDrop={isPublicList ? () => {} : handleDrop}
        onDragEnd={isPublicList ? () => {} : handleDragEnd}
        onHideStatistic={isPublicList ? () => {} : handleHideStatistic}
        onShowStatistic={isPublicList ? () => {} : handleShowStatistic}
        onCategoryInfoClick={handleCategoryInfoClick}
        onIndicatorInfoClick={handleIndicatorInfoClick}
        indicatorKeysWithInfo={['priceTargetDifference', 'averageRecommendation']}
        listName={listName}
        onStartEdit={isPublicList ? () => {} : handleStartEdit}
        onCancelEdit={isPublicList ? () => {} : handleCancelEdit}
        onDoneEdit={isPublicList ? () => {} : handleDoneEdit}
        onAddTicker={isPublicList ? () => {} : handleAddTicker}
        onSave={handleSave}
        tempListName={tempListName}
        onTempListNameChange={setTempListName}
        userLists={isPublicList ? [] : userLists}
        currentListId={isPublicList ? undefined : (currentListId || undefined)}
        onListChange={isPublicList ? undefined : handleListChange}
        onCreateNewList={isPublicList ? undefined : handleCreateNewList}
        onCreateNewListWithName={isPublicList ? undefined : handleCreateNewListWithName}
        onDeleteList={handleDeleteList}
        showDeleteButton={!isPublicList && isAuthenticated && userLists.length > 1}
        onShareList={handleShareList}
        showShareButton={!isPublicList && isAuthenticated && currentListId !== null && !isCurrentListLocked}
        onRefreshUsageLimits={handleRefreshUsageLimits}
        tabs={tabs}
        showRefreshButton={false}
        showDelayedPricesText={false}
        showLastUpdatedDate={true}
      />
      
      {/* Footer */}
      <div className="mt-4 sm:mt-8">
        <Footer />
      </div>
    </div>
      
      {/* Delete Confirmation Modal */}
      <DeleteListConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        listName={listName}
      />
      
      {/* Error Dialog */}
      <ErrorDialog
        isOpen={isErrorDialogOpen}
        onClose={handleErrorDialogClose}
        title="Error"
        message={errorMessage}
      />
      
      {/* Share List Modal */}
      <ShareListModal
        isOpen={isShareModalOpen}
        onClose={handleShareModalClose}
        listId={currentListId}
        currentList={currentList}
        onUpdate={handleShareModalUpdate}
      />
      
      {/* Category Info Modal */}
      <CategoryInfoModal
        isOpen={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
        categoryName={selectedCategory}
      />
      
      {/* Indicator Info Modal */}
      <IndicatorInfoModal
        isOpen={isIndicatorModalOpen}
        onClose={handleCloseIndicatorModal}
        indicatorKey={selectedIndicatorKey}
      />
    </>
  );
};

export default ListAnalystViews;
